import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { Input, Button } from '../components/design-system';
import { BusinessUserPicker } from '../components/BusinessUserPicker';
import { HttpRequestBuilder, HttpRequestValue } from '../components/HttpRequestBuilder';
import { SchedulePicker } from '../components/SchedulePicker';
import { FieldMatcher } from '../components/dataLists/FieldMatcher';
import { DataListSyncRuns } from '../components/dataLists/DataListSyncRuns';
import { flattenSampleFields } from '../utils/flattenSample';
import {
  apiService,
  DataList,
  DataListColumn,
  DataListColumnType,
  DataListColumnsSource,
  DataListDataSource,
  DataListSystemModule,
  DataListSystemModuleInfo,
  DataListSyncRecurrence,
  DataListWebhookConfig,
  DataListWebhookFieldMapping,
} from '../services/api';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface DataListSchemaScreenProps {
  listId?: string;
}

// A working column shape for the builder; new columns have no server id/createdAt yet.
interface ColumnDraft {
  localId: string;
  id?: string;
  name: string;
  type: DataListColumnType;
  nullable: boolean;
  required: boolean;
  unique: boolean;
  min?: string;
  max?: string;
  enum?: string;
  regex?: string;
  relationTargetId?: string;
  relationOnDelete: 'cascade' | 'setNull' | 'restrict';
  relationMultiple?: boolean; // many-to-many/one-to-many — value is an array of target ids instead of one
  dateMode: 'date' | 'datetime';
  order: number;
  advancedOpen?: boolean; // local UI-only state — constraints collapsed by default to keep the builder compact
  repeaterFields?: RepeaterFieldDraft[]; // present iff type === 'repeater'
}

// A repeater sub-field draft — deliberately much lighter than ColumnDraft
// (name + scalar type only, no constraints editor) since a repeater row is
// meant to stay a simple flat record, not a nested schema builder.
interface RepeaterFieldDraft {
  localId: string;
  id?: string;
  name: string;
  type: 'text' | 'number' | 'boolean' | 'date';
  required: boolean;
}

const genRepeaterFieldId = () => `rfld_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const COLUMN_TYPES: DataListColumnType[] = [
  'text', 'number', 'boolean', 'date', 'phone', 'voice', 'image', 'video', 'object', 'relation', 'repeater',
];

const COLUMN_TYPE_ICONS: Record<DataListColumnType, any> = {
  text: 'text-outline',
  number: 'calculator-outline',
  boolean: 'toggle-outline',
  date: 'calendar-outline',
  phone: 'call-outline',
  voice: 'mic-outline',
  image: 'image-outline',
  video: 'videocam-outline',
  object: 'code-slash-outline',
  relation: 'git-network-outline',
  repeater: 'list-outline',
};

const genLocalId = () => `col_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const emptyColumn = (order: number): ColumnDraft => ({
  localId: genLocalId(),
  name: '',
  type: 'text',
  nullable: true,
  required: false,
  unique: false,
  relationOnDelete: 'restrict',
  dateMode: 'date',
  order,
});

export const DataListSchemaScreen: React.FC<DataListSchemaScreenProps> = ({ listId }) => {
  const styles = useThemedStyles(getStyles);
  const { currentBusiness } = useAppStore();
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const { t } = useLocale();
  const rtl = useRTLStyles();


  const isEdit = !!listId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Splits the long form into tabs — Webhook only appears once dataSource is
  // 'webhook'; jumping away from it if the user switches back to 'manual'
  // avoids landing on a now-hidden tab.
  const [activeSchemaTab, setActiveSchemaTab] = useState<'basic' | 'fields' | 'webhook' | 'runs' | 'access'>('basic');
  const [hasRecords, setHasRecords] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // "The AI understands this list": its records are searchable by meaning in the assistants.
  const [aiMeaning, setAiMeaning] = useState(false);
  const [savedAiMeaning, setSavedAiMeaning] = useState(false);
  const [columns, setColumns] = useState<ColumnDraft[]>([emptyColumn(0)]);
  // Extra fields layered alongside a system list's fixed adapter columns
  // (item 2) — a separate array from `columns`, edited via the same
  // ColumnDraft builder UI but saved through the custom-columns endpoint.
  const [customColumns, setCustomColumns] = useState<ColumnDraft[]>([]);
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [assignedDepartmentIds, setAssignedDepartmentIds] = useState<string[]>([]);

  // Shape: two independent axes. Locked once editing an existing list.
  const [columnsSource, setColumnsSource] = useState<DataListColumnsSource>('custom');
  const [dataSource, setDataSource] = useState<DataListDataSource>('manual');
  const [systemModule, setSystemModule] = useState<DataListSystemModule | undefined>(undefined);
  const [systemModules, setSystemModules] = useState<DataListSystemModuleInfo[]>([]);
  const [webhookRequest, setWebhookRequest] = useState<HttpRequestValue>({ url: '', method: 'GET', headers: {}, responseRootPath: '' });
  const [fieldMapping, setFieldMapping] = useState<DataListWebhookFieldMapping[]>([]);
  const [webhookTesting, setWebhookTesting] = useState(false);
  const [webhookSample, setWebhookSample] = useState<any[]>([]);
  // Periodic background sync — blank/0 = sync-on-read only (no background Task created).
  const [webhookSyncRecurrence, setWebhookSyncRecurrence] = useState<DataListSyncRecurrence | undefined>(undefined);
  const [webhookMaxRetries, setWebhookMaxRetries] = useState('');
  const [webhookRetryDelaySeconds, setWebhookRetryDelaySeconds] = useState('');
  const [webhookSyncTaskId, setWebhookSyncTaskId] = useState<string | undefined>(undefined);
  // Off by default — see DataListWebhookConfig.triggerOutboundWebhooks in api.ts.
  const [triggerOutboundWebhooks, setTriggerOutboundWebhooks] = useState(false);
  // system+webhook Orders only — genuine multi-item support. Distinct from
  // the single-SKU isProductSku mapping above: this points at a NESTED items
  // array within each raw webhook row instead of one flat per-row SKU. Blank
  // arrayPath = feature not configured (list stays on the flat/single-item
  // path). The 5 sub-paths together describe ONE array extraction — grouped
  // and framed as such below rather than as independent fields.
  const [itemsArrayPath, setItemsArrayPath] = useState('');
  const [itemsSkuPath, setItemsSkuPath] = useState('');
  const [itemsNamePath, setItemsNamePath] = useState('');
  const [itemsQuantityPath, setItemsQuantityPath] = useState('');
  const [itemsPricePath, setItemsPricePath] = useState('');

  // Switching to Items Mapping (multi-item) hides the single-item
  // "Product SKU field" pill in FieldMatcher above (showProductSkuPicker),
  // but a column could already carry isProductSku: true from before the
  // switch — clear it so the two SKU-mapping mechanisms can't silently both
  // be active in saved state, even though only one is visible on screen.
  useEffect(() => {
    if (!itemsArrayPath.trim()) return;
    setFieldMapping((prev) => (prev.some((m) => m.isProductSku) ? prev.map((m) => ({ ...m, isProductSku: false })) : prev));
  }, [itemsArrayPath]);

  // system+webhook Orders only — which field on the TARGET module (Products/
  // Contacts) to search against for each cross-module relation this list
  // resolves, decoupling "how the external system identifies a
  // product/contact" from DISLIX's own fixed fields (SKU/Primary
  // Identifier). Blank = use that relation's default field (unchanged
  // behavior). Options are populated from that module's own unique-marked
  // columns (fixed + customColumns) — see loadRelationFieldOptions.
  const [productSearchField, setProductSearchField] = useState('');
  const [contactSearchField, setContactSearchField] = useState('');
  const [relationFieldOptions, setRelationFieldOptions] = useState<{
    products: Array<{ id: string; name: string }>;
    contacts: Array<{ id: string; name: string }>;
  }>({ products: [], contacts: [] });
  const [productSearchFieldPickerOpen, setProductSearchFieldPickerOpen] = useState(false);
  const [contactSearchFieldPickerOpen, setContactSearchFieldPickerOpen] = useState(false);

  const [otherLists, setOtherLists] = useState<Array<{ _id: string; name: string }>>([]);
  const [typePickerFor, setTypePickerFor] = useState<string | null>(null);
  const [relationPickerFor, setRelationPickerFor] = useState<string | null>(null);
  const [onDeletePickerFor, setOnDeletePickerFor] = useState<string | null>(null);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [departmentPickerOpen, setDepartmentPickerOpen] = useState(false);

  const loadSystemModules = useCallback(async () => {
    try {
      const res = await apiService.getDataListSystemModules();
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setSystemModules(res.data);
      }
    } catch (err) {
      console.error('[DataListSchemaScreen] Failed to load system modules:', err);
    }
  }, []);

  const loadOtherLists = useCallback(async () => {
    try {
      const res = await apiService.getDataLists({ limit: 1000 });
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setOtherLists(res.data.filter((l) => l._id !== listId).map((l) => ({ _id: l._id, name: l.name })));
      }
    } catch (err) {
      console.error('[DataListSchemaScreen] Failed to load other lists:', err);
    }
  }, [listId]);

  // Relation search-field options (item: dynamic cross-module resolver) —
  // for each of Products/Contacts, the fixed adapter columns marked unique
  // (from getDataListSystemModules, already loaded via loadSystemModules)
  // UNION that module's own system-backed list's customColumns marked
  // unique (fetched here, since loadSystemModules only returns each
  // module's static schema, not a specific business's actual list document).
  const loadRelationFieldOptions = useCallback(async () => {
    try {
      const [productsRes, contactsRes] = await Promise.all([
        apiService.getDataLists({ systemModule: 'products', limit: 1 }),
        apiService.getDataLists({ systemModule: 'contacts', limit: 1 }),
      ]);
      const customColumnOptions = (res: typeof productsRes) =>
        res.statusCode === 1000 && Array.isArray(res.data) && res.data[0]?.customColumns
          ? res.data[0].customColumns.filter((c) => c.constraints?.unique).map((c) => ({ id: c.id, name: c.name }))
          : [];
      setRelationFieldOptions({
        products: customColumnOptions(productsRes),
        contacts: customColumnOptions(contactsRes),
      });
    } catch (err) {
      console.error('[DataListSchemaScreen] Failed to load relation field options:', err);
    }
  }, []);

  const loadList = useCallback(async () => {
    if (!listId) return;
    try {
      setLoading(true);
      const res = await apiService.getDataList(listId);
      if (res.statusCode === 1000 && res.data) {
        const list: DataList = res.data;
        setName(list.name);
        setDescription(list.description || '');
        setAiMeaning(!!list.aiMeaning);
        setSavedAiMeaning(!!list.aiMeaning);
        setHasRecords(list.hasRecords);
        setAssignedUserIds(list.assignedUserIds || []);
        setAssignedDepartmentIds(list.assignedDepartmentIds || []);
        setColumnsSource(list.columnsSource || 'custom');
        setDataSource(list.dataSource || 'manual');
        setSystemModule(list.systemModule);
        if (list.webhookConfig) {
          setWebhookRequest({
            url: list.webhookConfig.url,
            method: list.webhookConfig.method,
            headers: list.webhookConfig.headers || {},
            responseRootPath: list.webhookConfig.responseRootPath || '',
          });
          setFieldMapping(list.webhookConfig.fieldMapping || []);
          setWebhookSyncRecurrence(list.webhookConfig.syncRecurrence);
          setWebhookMaxRetries(list.webhookConfig.maxRetries ? String(list.webhookConfig.maxRetries) : '');
          setWebhookRetryDelaySeconds(list.webhookConfig.retryDelaySeconds ? String(list.webhookConfig.retryDelaySeconds) : '');
          setWebhookSyncTaskId(list.webhookConfig.syncTaskId);
          if (list.webhookConfig.itemsMapping) {
            setItemsArrayPath(list.webhookConfig.itemsMapping.arrayPath || '');
            setItemsSkuPath(list.webhookConfig.itemsMapping.skuPath || '');
            setItemsNamePath(list.webhookConfig.itemsMapping.namePath || '');
            setItemsQuantityPath(list.webhookConfig.itemsMapping.quantityPath || '');
            setItemsPricePath(list.webhookConfig.itemsMapping.pricePath || '');
          }
          setProductSearchField(list.webhookConfig.relationTargets?.products?.searchField || '');
          setContactSearchField(list.webhookConfig.relationTargets?.contacts?.searchField || '');
          setTriggerOutboundWebhooks(!!list.webhookConfig.triggerOutboundWebhooks);
        }
        const toDraft = (c: DataListColumn, i: number): ColumnDraft => ({
          localId: genLocalId(),
          id: c.id,
          name: c.name,
          type: c.type,
          nullable: c.nullable,
          required: c.constraints?.required || false,
          unique: c.constraints?.unique || false,
          min: c.constraints?.min != null ? String(c.constraints.min) : undefined,
          max: c.constraints?.max != null ? String(c.constraints.max) : undefined,
          enum: c.constraints?.enum?.join(', '),
          regex: c.constraints?.regex,
          relationTargetId: c.relation?.targetListId,
          relationOnDelete: c.relation?.onDelete || 'restrict',
          relationMultiple: c.relation?.multiple || false,
          dateMode: c.constraints?.dateMode || 'date',
          order: i,
          repeaterFields: c.repeater?.fields?.map((f) => ({
            localId: genRepeaterFieldId(),
            id: f.id,
            name: f.name,
            type: f.type,
            required: f.constraints?.required || false,
          })),
        });
        setColumns([...list.columns].sort((a, b) => a.order - b.order).map(toDraft));
        setCustomColumns([...(list.customColumns || [])].sort((a, b) => a.order - b.order).map(toDraft));
      } else {
        showNotification('error', t('dataLists.notifications.errorTitle'), t('dataLists.schema.loadFailed'));
      }
    } catch (err: any) {
      showNotification('error', t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.schema.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [listId, showNotification, t]);

  useEffect(() => {
    loadOtherLists();
    loadSystemModules();
    loadRelationFieldOptions();
    if (isEdit) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  // Fixed adapter columns marked unique, UNION that module's unique
  // customColumns (relationFieldOptions) — the full set of valid relation
  // search-field choices for a given target module.
  const relationSearchFieldChoices = (module: 'products' | 'contacts'): Array<{ id: string; name: string }> => {
    const fixed = (systemModules.find((m) => m.module === module)?.columns || [])
      .filter((c) => c.constraints?.unique)
      .map((c) => ({ id: c.id, name: c.name }));
    return [...fixed, ...relationFieldOptions[module]];
  };

  const updateColumn = (localId: string, patch: Partial<ColumnDraft>) => {
    setColumns((prev) => prev.map((c) => (c.localId === localId ? { ...c, ...patch } : c)));
  };

  const addColumn = () => setColumns((prev) => [...prev, emptyColumn(prev.length)]);

  const removeColumn = (localId: string) => {
    setColumns((prev) => prev.filter((c) => c.localId !== localId).map((c, i) => ({ ...c, order: i })));
  };

  const moveColumn = (localId: string, direction: -1 | 1) => {
    setColumns((prev) => {
      const idx = prev.findIndex((c) => c.localId === localId);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((c, i) => ({ ...c, order: i }));
    });
  };

  // Mirror the above four helpers for customColumns (system lists' extra fields).
  const addCustomColumn = () => setCustomColumns((prev) => [...prev, emptyColumn(prev.length)]);
  const updateCustomColumn = (localId: string, patch: Partial<ColumnDraft>) => {
    setCustomColumns((prev) => prev.map((c) => (c.localId === localId ? { ...c, ...patch } : c)));
  };
  const removeCustomColumn = (localId: string) => {
    setCustomColumns((prev) => prev.filter((c) => c.localId !== localId).map((c, i) => ({ ...c, order: i })));
  };
  const moveCustomColumn = (localId: string, direction: -1 | 1) => {
    setCustomColumns((prev) => {
      const idx = prev.findIndex((c) => c.localId === localId);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((c, i) => ({ ...c, order: i }));
    });
  };

  const buildColumnPayload = (c: ColumnDraft): Omit<DataListColumn, 'createdAt'> => {
    const constraints: DataListColumn['constraints'] = {};
    if (c.required) constraints.required = true;
    if (c.unique) constraints.unique = true;
    if (c.min != null && c.min !== '') constraints.min = Number(c.min);
    if (c.max != null && c.max !== '') constraints.max = Number(c.max);
    if (c.enum && c.enum.trim()) constraints.enum = c.enum.split(',').map((s) => s.trim()).filter(Boolean);
    if (c.regex && c.regex.trim()) constraints.regex = c.regex.trim();
    if (c.type === 'date') constraints.dateMode = c.dateMode;

    const payload: Omit<DataListColumn, 'createdAt'> = {
      // For a not-yet-saved column, this is the same localId already used to
      // build any webhook fieldMapping referencing it (see activeColumns'
      // `c.id || c.localId` fallback) — sending it here so the server
      // preserves it instead of minting a different one is what keeps that
      // mapping from being orphaned the moment the column is actually saved.
      id: c.id || c.localId,
      name: c.name.trim(),
      type: c.type,
      nullable: c.nullable,
      order: c.order,
    };
    if (Object.keys(constraints).length > 0) payload.constraints = constraints;
    if (c.type === 'relation' && c.relationTargetId) {
      payload.relation = { targetListId: c.relationTargetId, onDelete: c.relationOnDelete, multiple: c.relationMultiple || undefined };
    }
    if (c.type === 'repeater') {
      payload.repeater = {
        fields: (c.repeaterFields || []).filter((f) => f.name.trim()).map((f) => ({
          id: f.id || genRepeaterFieldId(),
          name: f.name.trim(),
          type: f.type,
          constraints: f.required ? { required: true } : undefined,
        })),
      };
    }
    return payload;
  };

  const validate = (): string | null => {
    if (!name.trim()) return t('dataLists.schema.validation.nameRequired');
    if (columnsSource === 'custom') {
      if (columns.length === 0) return t('dataLists.schema.validation.columnRequired');
      if (columns.some((c) => !c.name.trim())) return t('dataLists.schema.validation.columnNameRequired');
      if (columns.some((c) => c.type === 'relation' && !c.relationTargetId)) {
        return t('dataLists.schema.validation.relationTargetRequired');
      }
    } else if (!systemModule) {
      return t('dataLists.schema.validation.systemModuleRequired');
    }
    if (dataSource === 'webhook') {
      if (!webhookRequest.url.trim()) return t('dataLists.schema.validation.webhookUrlRequired');
      if (fieldMapping.length === 0) return t('dataLists.schema.validation.fieldMappingRequired');
    }
    return null;
  };

  const activeColumns = (): DataListColumn[] =>
    columnsSource === 'system' && systemModule
      ? [
          ...(systemModules.find((m) => m.module === systemModule)?.columns || []),
          // Custom fields (item 2) are mappable webhook targets too, alongside
          // the module's own fixed columns.
          ...customColumns.map((c) => ({ ...buildColumnPayload(c), id: c.id || c.localId, createdAt: '' as any })),
        ]
      : columns.map((c) => ({ ...buildColumnPayload(c), id: c.id || c.localId, createdAt: '' as any }));

  const sourceFields = useMemo(() => flattenSampleFields(webhookSample), [webhookSample]);

  // Orders and Quotations share the items + linked-contact shape (see the
  // public POST /api/v1/orders and /api/v1/quotations), so both get the
  // items mapping, product SKU and contact relation pickers.
  const isOrderLikeModule = systemModule === 'orders' || systemModule === 'quotations';

  const buildWebhookConfig = (): DataListWebhookConfig => ({
    url: webhookRequest.url.trim(),
    method: webhookRequest.method,
    headers: Object.keys(webhookRequest.headers).length > 0 ? webhookRequest.headers : undefined,
    responseRootPath: (webhookRequest.responseRootPath || '').trim() || undefined,
    fieldMapping,
    syncRecurrence: webhookSyncRecurrence,
    maxRetries: webhookMaxRetries.trim() ? parseInt(webhookMaxRetries, 10) : undefined,
    retryDelaySeconds: webhookRetryDelaySeconds.trim() ? parseInt(webhookRetryDelaySeconds, 10) : undefined,
    itemsMapping: columnsSource === 'system' && isOrderLikeModule && itemsArrayPath.trim() && itemsQuantityPath.trim()
      ? {
          arrayPath: itemsArrayPath.trim(),
          skuPath: itemsSkuPath.trim() || undefined,
          namePath: itemsNamePath.trim() || undefined,
          quantityPath: itemsQuantityPath.trim(),
          pricePath: itemsPricePath.trim() || undefined,
        }
      : undefined,
    relationTargets: columnsSource === 'system' && isOrderLikeModule && (productSearchField || contactSearchField)
      ? {
          products: productSearchField ? { searchField: productSearchField } : undefined,
          contacts: contactSearchField ? { searchField: contactSearchField } : undefined,
        }
      : undefined,
    triggerOutboundWebhooks,
  });

  const [resyncingColumns, setResyncingColumns] = useState(false);

  const handleResyncSystemColumns = async () => {
    if (!listId) return;
    try {
      setResyncingColumns(true);
      const res = await apiService.resyncDataListSystemColumns(listId);
      if (res.statusCode === 1000) {
        showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.schema.resyncColumnsSuccess'));
      } else {
        throw new Error(res.errors?.[0]?.message || t('dataLists.schema.resyncColumnsFailed'));
      }
    } catch (err: any) {
      showNotification('error', t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.schema.resyncColumnsFailed'));
    } finally {
      setResyncingColumns(false);
    }
  };

  const handleTestWebhook = async () => {
    if (!webhookRequest.url.trim()) {
      showNotification('error', t('dataLists.notifications.errorTitle'), t('dataLists.schema.validation.webhookUrlRequired'));
      return;
    }
    try {
      setWebhookTesting(true);
      const res = await apiService.testDataListWebhookConfig({
        url: webhookRequest.url.trim(),
        method: webhookRequest.method,
        headers: Object.keys(webhookRequest.headers).length > 0 ? webhookRequest.headers : undefined,
        responseRootPath: (webhookRequest.responseRootPath || '').trim() || undefined,
        fieldMapping,
      });
      if (res.statusCode === 1000 && res.data) {
        setWebhookSample(res.data.rawSample || []);
        showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.schema.webhook.testSuccess'));
      } else {
        throw new Error(res.errors?.[0]?.message || t('dataLists.schema.webhook.testFailed'));
      }
    } catch (err: any) {
      showNotification('error', t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.schema.webhook.testFailed'));
    } finally {
      setWebhookTesting(false);
    }
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      showNotification('error', t('dataLists.notifications.errorTitle'), validationError);
      return;
    }
    try {
      setSaving(true);
      if (isEdit && listId) {
        // Update metadata first, then sync columns, THEN the webhook config —
        // in that order. webhookConfig.fieldMapping can reference a column
        // that's being added/changed in THIS SAME save; saving webhookConfig
        // first (the previous order) validated/persisted a mapping against
        // columns that hadn't been created/updated yet, which is exactly the
        // "add/map/remove a custom field on a system-module list" failure —
        // columns must be settled server-side before anything referencing
        // their ids is submitted.
        const updated = await apiService.updateDataList(listId, { name: name.trim(), description: description.trim() || undefined,
          ...(aiMeaning !== savedAiMeaning ? { aiMeaning } : {}) });
        // Turning AI understanding on is refused for lists that are too large; say why instead of saving silently.
        if (aiMeaning !== savedAiMeaning) {
          if (updated.statusCode !== 1000) throw new Error(updated.errors?.[0]?.message || t('dataLists.schema.aiMeaningFailed'));
          setSavedAiMeaning(aiMeaning);
        }

        // Re-fetch immediately before building ops (not just for the removeOps diff) — the
        // screen's `columns`/`customColumns` state was populated by loadList() at MOUNT
        // time, which can be stale by save time (e.g. edited in another tab/device
        // meanwhile). Building updateOps from that stale state sends ids the server no
        // longer has, which the PATCH correctly rejects as "Column not found" rather than
        // silently misapplying. Cross-checking against this fresh fetch means an id that's
        // vanished server-side gets treated as a brand-new column (re-added) instead of
        // failing the whole save.
        const original = await apiService.getDataList(listId);

        if (columnsSource === 'system') {
          // Fixed columns are immutable — but customColumns (item 2's extra
          // fields) still sync via the same add/update/remove diff pattern
          // used below for fully-custom lists, just against the dedicated
          // custom-columns endpoint.
          const originalCustom = original.statusCode === 1000 && original.data ? (original.data.customColumns || []) : [];
          const originalCustomIds = new Set(originalCustom.map((oc) => oc.id));
          const currentCustomIds = new Set(customColumns.filter((c) => c.id).map((c) => c.id));
          const removeCustomOps = originalCustom
            .filter((oc) => !currentCustomIds.has(oc.id))
            .map((oc) => ({ op: 'remove' as const, id: oc.id }));
          const addCustomOps = customColumns
            .filter((c) => !c.id || !originalCustomIds.has(c.id))
            .map((c) => ({ op: 'add' as const, ...buildColumnPayload(c) }));
          const updateCustomOps = customColumns
            .filter((c) => c.id && originalCustomIds.has(c.id))
            .map((c) => {
              const payload = buildColumnPayload(c);
              // id and type are both immutable on update — id in particular
              // must come from c.id (the already-persisted id), never from
              // payload.id (which falls back to localId for a brand-new
              // column, irrelevant here since this branch only ever handles
              // already-saved columns).
              const { type: _type, id: _id, ...rest } = payload;
              return { op: 'update' as const, id: c.id as string, ...rest };
            });
          const customOps = [...addCustomOps, ...updateCustomOps, ...removeCustomOps];
          if (customOps.length > 0) {
            const customColRes = await apiService.updateDataListCustomColumns(listId, { ops: customOps });
            if (customColRes.statusCode !== 1000) {
              throw new Error(customColRes.errors?.[0]?.message || t('dataLists.schema.saveFailed'));
            }
          }

          if (dataSource === 'webhook') {
            const webhookRes = await apiService.updateDataListWebhookConfig(listId, buildWebhookConfig());
            if (webhookRes.statusCode !== 1000) {
              throw new Error(webhookRes.errors?.[0]?.message || t('dataLists.schema.saveFailed'));
            }
          }

          await apiService.assignDataList(listId, { userIds: assignedUserIds, departmentIds: assignedDepartmentIds });
          showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.schema.updateSuccess'));
          navigateTo('data-lists');
          return;
        }

        const originalColumns = original.statusCode === 1000 && original.data ? original.data.columns : [];
        const originalIds = new Set(originalColumns.map((oc) => oc.id));
        const currentIds = new Set(columns.filter((c) => c.id).map((c) => c.id));
        const removeOps = originalColumns
          .filter((oc) => !currentIds.has(oc.id))
          .map((oc) => ({ op: 'remove' as const, id: oc.id }));
        const addOps = columns
          .filter((c) => !c.id || !originalIds.has(c.id))
          .map((c) => ({ op: 'add' as const, ...buildColumnPayload(c) }));
        const updateOps = columns
          .filter((c) => c.id && originalIds.has(c.id))
          .map((c) => {
            const payload = buildColumnPayload(c);
            // id and type are both immutable on update — see the matching
            // comment on updateCustomOps above.
            const { type: _type, id: _id, ...rest } = payload;
            return { op: 'update' as const, id: c.id as string, ...rest };
          });
        const ops = [...addOps, ...updateOps, ...removeOps];

        if (ops.length > 0) {
          const colRes = await apiService.updateDataListColumns(listId, { ops });
          if (colRes.statusCode !== 1000) {
            throw new Error(colRes.errors?.[0]?.message || t('dataLists.schema.saveFailed'));
          }
        }

        if (dataSource === 'webhook') {
          const webhookRes = await apiService.updateDataListWebhookConfig(listId, buildWebhookConfig());
          if (webhookRes.statusCode !== 1000) {
            throw new Error(webhookRes.errors?.[0]?.message || t('dataLists.schema.saveFailed'));
          }
        }

        await apiService.assignDataList(listId, { userIds: assignedUserIds, departmentIds: assignedDepartmentIds });
        showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.schema.updateSuccess'));
        navigateTo('data-lists');
      } else {
        const res = await apiService.createDataList({
          name: name.trim(),
          description: description.trim() || undefined,
          columns: columnsSource === 'custom' ? columns.map(buildColumnPayload) : undefined,
          customColumns: columnsSource === 'system' && customColumns.length > 0
            ? customColumns.map((c) => ({ op: 'add' as const, ...buildColumnPayload(c) }))
            : undefined,
          columnsSource,
          dataSource,
          systemModule: columnsSource === 'system' ? systemModule : undefined,
          webhookConfig: dataSource === 'webhook' ? buildWebhookConfig() : undefined,
          assignedUserIds,
          assignedDepartmentIds,
        });
        if (res.statusCode === 1000 && res.data) {
          showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.schema.createSuccess'));
          navigateTo('data-lists');
        } else {
          throw new Error(res.errors?.[0]?.message || t('dataLists.schema.saveFailed'));
        }
      }
    } catch (err: any) {
      showNotification('error', t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.schema.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = (type: DataListColumnType) => t(`dataLists.type.${type}` as any);

  const renderColumn = (
    col: ColumnDraft,
    index: number,
    total: number = columns.length,
    handlers: { update: typeof updateColumn; remove: typeof removeColumn; move: typeof moveColumn } = { update: updateColumn, remove: removeColumn, move: moveColumn }
  ) => {
    const { update: updateColumn_, remove: removeColumn_, move: moveColumn_ } = handlers;
    const typeLocked = isEdit && hasRecords && !!col.id;
    return (
      <View key={col.localId} style={styles.columnCard}>
        <View style={[styles.columnHeader, { flexDirection: rtl.rowDirection }]}>
          <Text style={styles.columnIndex}>#{index + 1}</Text>
          <View style={{ flex: 1 }} />
          <TouchableOpacity onPress={() => moveColumn_(col.localId, -1)} disabled={index === 0} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="chevron-up" size={20} color={index === 0 ? colors.textLight : colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => moveColumn_(col.localId, 1)} disabled={index === total - 1} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="chevron-down" size={20} color={index === total - 1 ? colors.textLight : colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => removeColumn_(col.localId)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} style={rtl.marginStart(spacing.sm)}>
            <Ionicons name="trash-outline" size={18} color={colors.error} />
          </TouchableOpacity>
        </View>

        <Input
          value={col.name}
          onChangeText={(text) => updateColumn_(col.localId, { name: text })}
          placeholder={t('dataLists.schema.columnNamePlaceholder')}
        />

        {/* Type picker — a horizontal row of icon chips (always visible, no
            tap-to-expand step) instead of a select-button that opens a
            9-row inline list; simplifies the most common action (picking a
            type) down to a single tap. */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.typeChipsScroll} contentContainerStyle={[styles.typeChipsRow, { flexDirection: rtl.rowDirection }]}>
          {COLUMN_TYPES.map((tp) => {
            const active = col.type === tp;
            return (
              <TouchableOpacity
                key={tp}
                style={[styles.typeChip, active && styles.typeChipActive, typeLocked && !active && styles.typeChipDisabled]}
                onPress={() => !typeLocked && updateColumn_(col.localId, { type: tp })}
                disabled={typeLocked}
              >
                <Ionicons name={COLUMN_TYPE_ICONS[tp]} size={16} color={active ? colors.white : colors.text.secondary} />
                <Text style={[styles.typeChipText, active && styles.typeChipTextActive]}>{typeLabel(tp)}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
        {typeLocked && <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.typeLocked')}</Text>}

        {/* Nullable/Required/Unique condensed into one row of compact
            toggle pills instead of three stacked full-width Toggle rows. */}
        <View style={[styles.flagsRow, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity style={[styles.flagPill, !col.nullable && styles.flagPillActive]} onPress={() => updateColumn_(col.localId, { nullable: !col.nullable })}>
            <Text style={[styles.flagPillText, !col.nullable && styles.flagPillTextActive]}>{t('dataLists.schema.notNullable')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.flagPill, col.required && styles.flagPillActive]} onPress={() => updateColumn_(col.localId, { required: !col.required })}>
            <Text style={[styles.flagPillText, col.required && styles.flagPillTextActive]}>{t('dataLists.schema.required')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.flagPill, col.unique && styles.flagPillActive]} onPress={() => updateColumn_(col.localId, { unique: !col.unique })}>
            <Text style={[styles.flagPillText, col.unique && styles.flagPillTextActive]}>{t('dataLists.schema.unique')}</Text>
          </TouchableOpacity>
        </View>

        {/* Date mode — kept always-visible (not tucked in Advanced) since it
            changes what the field renderer shows on the record form. */}
        {col.type === 'date' && (
          <View style={[styles.flagsRow, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity
              style={[styles.flagPill, col.dateMode === 'date' && styles.flagPillActive]}
              onPress={() => updateColumn_(col.localId, { dateMode: 'date' })}
            >
              <Text style={[styles.flagPillText, col.dateMode === 'date' && styles.flagPillTextActive]}>{t('dataLists.schema.dateOnly')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.flagPill, col.dateMode === 'datetime' && styles.flagPillActive]}
              onPress={() => updateColumn_(col.localId, { dateMode: 'datetime' })}
            >
              <Text style={[styles.flagPillText, col.dateMode === 'datetime' && styles.flagPillTextActive]}>{t('dataLists.schema.dateTime')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Relation config — kept always-visible when type==='relation' since
            it's not optional (a relation column is meaningless without a
            target), unlike the constraints below. */}
        {col.type === 'relation' && (
          <>
            <TouchableOpacity style={[styles.selectButton, { flexDirection: rtl.rowDirection }]} onPress={() => setRelationPickerFor(relationPickerFor === col.localId ? null : col.localId)}>
              <Text style={[styles.selectButtonText, !col.relationTargetId && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]}>
                {col.relationTargetId ? (otherLists.find((l) => l._id === col.relationTargetId)?.name || t('dataLists.schema.relationTargetPlaceholder')) : t('dataLists.schema.relationTargetPlaceholder')}
              </Text>
              <Ionicons name="chevron-down" size={18} color={colors.textLight} />
            </TouchableOpacity>
            {relationPickerFor === col.localId && (
              <View style={styles.inlineOptions}>
                {otherLists.map((l) => (
                  <TouchableOpacity key={l._id} style={styles.inlineOption} onPress={() => { updateColumn_(col.localId, { relationTargetId: l._id }); setRelationPickerFor(null); }}>
                    <Text style={[styles.inlineOptionText, col.relationTargetId === l._id && styles.inlineOptionTextActive]}>{l.name}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <TouchableOpacity style={[styles.selectButton, { flexDirection: rtl.rowDirection }]} onPress={() => setOnDeletePickerFor(onDeletePickerFor === col.localId ? null : col.localId)}>
              <Text style={[styles.selectButtonText, { textAlign: rtl.textStart }]}>{t(`dataLists.schema.onDelete.${col.relationOnDelete}` as any)}</Text>
              <Ionicons name="chevron-down" size={18} color={colors.textLight} />
            </TouchableOpacity>
            {onDeletePickerFor === col.localId && (
              <View style={styles.inlineOptions}>
                {(['cascade', 'setNull', 'restrict'] as const).map((od) => (
                  <TouchableOpacity key={od} style={styles.inlineOption} onPress={() => { updateColumn_(col.localId, { relationOnDelete: od }); setOnDeletePickerFor(null); }}>
                    <Text style={[styles.inlineOptionText, col.relationOnDelete === od && styles.inlineOptionTextActive]}>{t(`dataLists.schema.onDelete.${od}` as any)}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {/* Many-to-many/one-to-many — value becomes an array of target ids
                instead of one. Locked once the list has records, same rule as
                the column type itself (decision #7), since switching this
                after data exists would silently change how existing values
                are shaped. */}
            <View style={[styles.flagsRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.flagPill, col.relationMultiple && styles.flagPillActive]}
                onPress={() => !hasRecords && updateColumn_(col.localId, { relationMultiple: !col.relationMultiple })}
                disabled={hasRecords}
              >
                <Text style={[styles.flagPillText, col.relationMultiple && styles.flagPillTextActive]}>{t('dataLists.schema.relationMultiple')}</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        {/* Repeater sub-fields — always-visible when type==='repeater' since a
            repeater with zero sub-fields is meaningless, same reasoning as
            relation's always-visible target picker above. */}
        {col.type === 'repeater' && (
          <View style={styles.repeaterBuilder}>
            <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.repeaterFieldsHint')}</Text>
            {(col.repeaterFields || []).map((rf) => (
              <View key={rf.localId} style={[styles.repeaterFieldRow, { flexDirection: rtl.rowDirection }]}>
                <View style={{ flex: 1 }}>
                  <Input
                    value={rf.name}
                    onChangeText={(v) => {
                      const next = (col.repeaterFields || []).map((f) => (f.localId === rf.localId ? { ...f, name: v } : f));
                      updateColumn_(col.localId, { repeaterFields: next });
                    }}
                    placeholder={t('dataLists.schema.repeaterFieldNamePlaceholder')}
                  />
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.repeaterFieldTypeScroll}>
                  {(['text', 'number', 'boolean', 'date'] as const).map((ft) => {
                    const active = rf.type === ft;
                    return (
                      <TouchableOpacity
                        key={ft}
                        style={[styles.typeChip, active && styles.typeChipActive]}
                        onPress={() => {
                          const next = (col.repeaterFields || []).map((f) => (f.localId === rf.localId ? { ...f, type: ft } : f));
                          updateColumn_(col.localId, { repeaterFields: next });
                        }}
                      >
                        <Text style={[styles.typeChipText, active && styles.typeChipTextActive]}>{typeLabel(ft)}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
                <TouchableOpacity
                  onPress={() => {
                    const next = (col.repeaterFields || []).filter((f) => f.localId !== rf.localId);
                    updateColumn_(col.localId, { repeaterFields: next });
                  }}
                  style={styles.repeaterFieldRemove}
                >
                  <Ionicons name="close-circle" size={20} color={colors.error} />
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity
              style={[styles.addColumnButton, { flexDirection: rtl.rowDirection }]}
              onPress={() => {
                const next = [...(col.repeaterFields || []), { localId: genRepeaterFieldId(), name: '', type: 'text' as const, required: false }];
                updateColumn_(col.localId, { repeaterFields: next });
              }}
            >
              <Ionicons name="add" size={16} color={colors.primary as string} />
              <Text style={styles.addColumnText}>{t('dataLists.schema.addRepeaterField')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Advanced constraints (min/max/enum/regex) — collapsed by default;
            most columns need none of these, so hiding them behind a single
            disclosure keeps the common case (name + type + flags) compact. */}
        {(col.type === 'number' || col.type === 'text' || col.type === 'date') && (
          <>
            <TouchableOpacity
              style={[styles.advancedToggle, { flexDirection: rtl.rowDirection }]}
              onPress={() => updateColumn_(col.localId, { advancedOpen: !col.advancedOpen })}
            >
              <Ionicons name={col.advancedOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary} />
              <Text style={styles.advancedToggleText}>{t('dataLists.schema.advanced')}</Text>
            </TouchableOpacity>
            {col.advancedOpen && (
              <View style={styles.advancedSection}>
                <View style={[styles.minMaxRow, { flexDirection: rtl.rowDirection }]}>
                  <View style={{ flex: 1 }}>
                    <Input label={t('dataLists.schema.min')} value={col.min || ''} onChangeText={(v) => updateColumn_(col.localId, { min: v })} keyboardType="numeric" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Input label={t('dataLists.schema.max')} value={col.max || ''} onChangeText={(v) => updateColumn_(col.localId, { max: v })} keyboardType="numeric" />
                  </View>
                </View>
                {col.type === 'text' && (
                  <>
                    <Input label={t('dataLists.schema.enum')} value={col.enum || ''} onChangeText={(v) => updateColumn_(col.localId, { enum: v })} placeholder={t('dataLists.schema.enumPlaceholder')} autoCapitalize="none" />
                    <Input label={t('dataLists.schema.regex')} value={col.regex || ''} onChangeText={(v) => updateColumn_(col.localId, { regex: v })} placeholder={t('dataLists.schema.regexPlaceholder')} autoCapitalize="none" />
                  </>
                )}
              </View>
            )}
          </>
        )}
      </View>
    );
  };

  // The Webhook tab only exists while dataSource === 'webhook' — if the user
  // switches back to 'manual' while on it, fall back to 'basic' for both the
  // tab bar's highlight and the content gating below, so the body never goes
  // blank on a now-hidden tab.
  // Runs needs a saved list with its sync task.
  const showRunsTab = isEdit && dataSource === 'webhook' && !!webhookSyncTaskId;
  const effectiveSchemaTab =
    (activeSchemaTab === 'webhook' && dataSource !== 'webhook') || (activeSchemaTab === 'runs' && !showRunsTab) ? 'basic' : activeSchemaTab;

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      formHeader={
        <>
          <FormHeader
            title={isEdit ? t('dataLists.schema.editTitle') : t('dataLists.schema.createTitle')}
            onBack={() => navigateTo('data-lists')}
            actions={[{ icon: 'checkmark', primary: true, onPress: handleSave, loading: saving, disabled: saving, accessibilityLabel: t('dataLists.schema.save') }]}
          />
          {!loading && (() => {
            const tabs: Array<{ id: typeof activeSchemaTab; label: string }> = [
              { id: 'basic', label: t('dataLists.schema.tabs.basic') },
              { id: 'fields', label: t('dataLists.schema.tabs.fields') },
              ...(dataSource === 'webhook' ? [{ id: 'webhook' as const, label: t('dataLists.schema.tabs.webhook') }] : []),
              ...(showRunsTab ? [{ id: 'runs' as const, label: t('dataLists.schema.tabs.runs') }] : []),
              { id: 'access', label: t('dataLists.schema.tabs.access') },
            ];
            return (
              <View style={[styles.schemaTabsRow, { flexDirection: rtl.rowDirection }]}>
                {tabs.map((tb) => (
                  <TouchableOpacity
                    key={tb.id}
                    style={[styles.schemaTab, effectiveSchemaTab === tb.id && styles.schemaTabActive]}
                    onPress={() => setActiveSchemaTab(tb.id)}
                  >
                    <Text style={[styles.schemaTabText, effectiveSchemaTab === tb.id && styles.schemaTabTextActive]}>{tb.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            );
          })()}
        </>
      }
    >
      <View style={styles.container}>
        {loading ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <>
          <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} showsVerticalScrollIndicator={false}>
            {effectiveSchemaTab === 'basic' && (
            <>
            <Input label={t('dataLists.schema.nameLabel')} value={name} onChangeText={setName} placeholder={t('dataLists.schema.namePlaceholder')} />
            <Input label={t('dataLists.schema.descriptionLabel')} value={description} onChangeText={setDescription} placeholder={t('dataLists.schema.descriptionPlaceholder')} multiline />
            {isEdit && columnsSource === 'custom' && dataSource === 'manual' && (
              <View style={[styles.webhookToggleRow, { flexDirection: rtl.rowDirection }]}>
                <View style={styles.webhookToggleInfo}>
                  <Text style={[styles.webhookToggleLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.aiMeaningLabel')}</Text>
                  <Text style={[styles.webhookToggleHint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.aiMeaningHint')}</Text>
                </View>
                <Switch
                  value={aiMeaning}
                  onValueChange={setAiMeaning}
                  trackColor={{ false: colors.border, true: (colors.primary as any) + '60' }}
                  thumbColor={aiMeaning ? colors.primary : colors.textLight}
                />
              </View>
            )}

            {/* Shape picker — two independent axes, locked once the list exists. */}
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, marginTop: spacing.lg }]}>{t('dataLists.schema.shapeSection')}</Text>
            <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.columnsSourceLabel')}</Text>
            <View style={[styles.flagsRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.flagPill, columnsSource === 'custom' && styles.flagPillActive, isEdit && styles.typeChipDisabled]}
                disabled={isEdit}
                onPress={() => setColumnsSource('custom')}
              >
                <Text style={[styles.flagPillText, columnsSource === 'custom' && styles.flagPillTextActive]}>{t('dataLists.schema.columnsSource.custom')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.flagPill, columnsSource === 'system' && styles.flagPillActive, isEdit && styles.typeChipDisabled]}
                disabled={isEdit}
                onPress={() => setColumnsSource('system')}
              >
                <Text style={[styles.flagPillText, columnsSource === 'system' && styles.flagPillTextActive]}>{t('dataLists.schema.columnsSource.system')}</Text>
              </TouchableOpacity>
            </View>

            <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.dataSourceLabel')}</Text>
            <View style={[styles.flagsRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.flagPill, dataSource === 'manual' && styles.flagPillActive, isEdit && styles.typeChipDisabled]}
                disabled={isEdit}
                onPress={() => setDataSource('manual')}
              >
                <Text style={[styles.flagPillText, dataSource === 'manual' && styles.flagPillTextActive]}>{t('dataLists.schema.dataSource.manual')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.flagPill, dataSource === 'webhook' && styles.flagPillActive, isEdit && styles.typeChipDisabled]}
                disabled={isEdit}
                onPress={() => setDataSource('webhook')}
              >
                <Text style={[styles.flagPillText, dataSource === 'webhook' && styles.flagPillTextActive]}>{t('dataLists.schema.dataSource.webhook')}</Text>
              </TouchableOpacity>
            </View>
            {isEdit && <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.shapeLocked')}</Text>}

            {columnsSource === 'system' && (
              <>
                <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.systemModuleLabel')}</Text>
                <View style={[styles.typeChipsRow, { flexDirection: rtl.rowDirection, flexWrap: 'wrap' }]}>
                  {systemModules.map((m) => (
                    <TouchableOpacity
                      key={m.module}
                      style={[styles.typeChip, systemModule === m.module && styles.typeChipActive, isEdit && styles.typeChipDisabled]}
                      disabled={isEdit}
                      onPress={() => setSystemModule(m.module)}
                    >
                      <Text style={[styles.typeChipText, systemModule === m.module && styles.typeChipTextActive]}>{t(`dataLists.schema.systemModule.${m.module}` as any)}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {systemModule && (
                  <Text style={[styles.hint, { textAlign: rtl.textStart }]}>
                    {t('dataLists.schema.systemModuleColumnsHint', { count: systemModules.find((m) => m.module === systemModule)?.columns.length || 0 })}
                  </Text>
                )}
                {/* This list's stored columns were computed once at creation
                    time — if the module later gained new columns (a code
                    change), this list won't see them until resynced. */}
                {isEdit && systemModule && (
                  <TouchableOpacity style={styles.resyncColumnsButton} onPress={handleResyncSystemColumns} disabled={resyncingColumns}>
                    {resyncingColumns ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Ionicons name="refresh-outline" size={16} color={colors.primary} />
                    )}
                    <Text style={styles.resyncColumnsText}>{t('dataLists.schema.resyncColumns')}</Text>
                  </TouchableOpacity>
                )}
              </>
            )}
            </>
            )}

            {effectiveSchemaTab === 'fields' && (
            <>
            {columnsSource === 'system' && (
              <>
                {/* Custom Fields — extra fields layered alongside the module's
                    own fixed columns (item 2). A distinct section, not merged
                    with the fixed-columns preview, since these are the only
                    ones the admin can actually add/edit/remove here. */}
                <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
                  <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('dataLists.schema.customColumnsSection')}</Text>
                  <TouchableOpacity style={[styles.addColumnButton, { flexDirection: rtl.rowDirection }]} onPress={addCustomColumn}>
                    <Ionicons name="add" size={18} color={colors.primary} />
                    <Text style={styles.addColumnText}>{t('dataLists.schema.addColumn')}</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.customColumnsHint')}</Text>
                {customColumns.map((col, index) =>
                  renderColumn(col, index, customColumns.length, { update: updateCustomColumn, remove: removeCustomColumn, move: moveCustomColumn })
                )}
              </>
            )}
            </>
            )}

            {effectiveSchemaTab === 'webhook' && dataSource === 'webhook' && (
            <>
            {dataSource === 'webhook' && (
              <View style={styles.columnCard}>
                <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, fontSize: typography.fontSize.base }]}>{t('dataLists.schema.webhook.title')}</Text>
                <HttpRequestBuilder
                  key={listId || 'new'}
                  value={webhookRequest}
                  onChange={setWebhookRequest}
                  urlPlaceholder="https://example.com/api/data"
                />

                <Button title={webhookTesting ? t('dataLists.schema.webhook.testing') : t('dataLists.schema.webhook.test')} onPress={handleTestWebhook} disabled={webhookTesting} loading={webhookTesting} variant="outline" style={{ marginTop: spacing.sm }} />

                <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, fontSize: typography.fontSize.sm, marginTop: spacing.md }]}>{t('dataLists.schema.webhook.mappingSection')}</Text>
                <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.mappingHint')}</Text>
                {webhookSample.length === 0 && (
                  <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.mapperNeedsSample')}</Text>
                )}
                <FieldMatcher
                  sourceFields={sourceFields}
                  targetColumns={activeColumns()}
                  value={fieldMapping}
                  onChange={setFieldMapping}
                  showExternalId={columnsSource === 'system'}
                  showOrderRelationPickers={columnsSource === 'system' && isOrderLikeModule}
                  // Hidden once Items Mapping (below) is configured — its
                  // own skuPath field is the same "which field is the
                  // product SKU" concept, and showing both let a list define
                  // the SKU two different, silently-conflicting ways.
                  showProductSkuPicker={columnsSource === 'system' && isOrderLikeModule && !itemsArrayPath.trim()}
                />
                {webhookSample.length > 0 && (
                  <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.sampleHint', { count: webhookSample.length })}</Text>
                )}

                {columnsSource === 'system' && isOrderLikeModule && (
                  <>
                    {/* Items Mapping — genuine multi-item support: one nested
                        array within each raw webhook row, not a flat per-row
                        SKU. The 5 sub-paths below are sub-fields of that SAME
                        array (not independent mappings), so they're grouped
                        in one bordered card with an "Array" badge to make
                        that single-unit relationship visible on screen.
                        Leave Array Path blank to skip multi-item support and
                        map a single product SKU per row via Field Mapping
                        above instead — there is no default-product fallback:
                        an unresolved row now fails the sync instead of
                        substituting a stand-in product. */}
                    <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, fontSize: typography.fontSize.sm, marginTop: spacing.md }]}>{t('dataLists.schema.webhook.itemsMappingSection')}</Text>
                    <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.itemsMappingHint')}</Text>
                    <View style={styles.arrayMappingCard}>
                      <View style={[styles.arrayMappingBadgeRow, { flexDirection: rtl.rowDirection }]}>
                        <Ionicons name="layers-outline" size={16} color={colors.primary as string} />
                        <Text style={styles.arrayMappingBadgeText}>{t('dataLists.schema.webhook.itemsMappingArrayBadge')}</Text>
                      </View>
                      <Input
                        label={t('dataLists.schema.webhook.itemsArrayPathLabel')}
                        value={itemsArrayPath}
                        onChangeText={setItemsArrayPath}
                        placeholder="items"
                        autoCapitalize="none"
                      />
                      {itemsArrayPath.trim().length > 0 && (
                        <>
                          <Text style={[styles.hint, { textAlign: rtl.textStart, marginTop: 0 }]}>{t('dataLists.schema.webhook.itemsMappingSubFieldsHint')}</Text>
                          <Input
                            label={t('dataLists.schema.webhook.itemsQuantityPathLabel')}
                            value={itemsQuantityPath}
                            onChangeText={setItemsQuantityPath}
                            placeholder="quantity"
                            autoCapitalize="none"
                          />
                          <Input
                            label={t('dataLists.schema.webhook.itemsSkuPathLabel')}
                            value={itemsSkuPath}
                            onChangeText={setItemsSkuPath}
                            placeholder="sku"
                            autoCapitalize="none"
                          />
                          <Input
                            label={t('dataLists.schema.webhook.itemsNamePathLabel')}
                            value={itemsNamePath}
                            onChangeText={setItemsNamePath}
                            placeholder="name"
                            autoCapitalize="none"
                          />
                          <Input
                            label={t('dataLists.schema.webhook.itemsPricePathLabel')}
                            value={itemsPricePath}
                            onChangeText={setItemsPricePath}
                            placeholder="price"
                            autoCapitalize="none"
                          />
                        </>
                      )}
                    </View>

                    {/* Relations — which field on the TARGET module (Products/
                        Contacts) to search against when resolving this list's
                        cross-module links. Decouples the external system's own
                        identifier scheme from DISLIX's fixed fields: a
                        business whose upstream system has no SKU concept (or
                        wants to key off a different field) can point the
                        search at a customColumn on that module's own system
                        list instead — configured there (Products/Contacts'
                        own schema screen), not here; this only picks WHICH
                        already-unique field to use. Blank = the pre-existing
                        default (SKU / Primary Identifier), unchanged
                        behavior. */}
                    <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, fontSize: typography.fontSize.sm, marginTop: spacing.md }]}>{t('dataLists.schema.webhook.relationsSection')}</Text>
                    <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.relationsHint')}</Text>

                    <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.relationProductsLabel')}</Text>
                    <TouchableOpacity
                      style={[styles.selectButton, { flexDirection: rtl.rowDirection }]}
                      onPress={() => setProductSearchFieldPickerOpen((v) => !v)}
                    >
                      <Text style={[styles.selectButtonText, !productSearchField && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]}>
                        {productSearchField
                          ? relationSearchFieldChoices('products').find((f) => f.id === productSearchField)?.name || productSearchField
                          : t('dataLists.schema.webhook.relationDefaultPlaceholder', { field: 'SKU' })}
                      </Text>
                      <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                    </TouchableOpacity>
                    {productSearchFieldPickerOpen && (
                      <View style={styles.inlineOptions}>
                        <TouchableOpacity style={styles.inlineOption} onPress={() => { setProductSearchField(''); setProductSearchFieldPickerOpen(false); }}>
                          <Text style={[styles.inlineOptionText, !productSearchField && styles.inlineOptionTextActive]}>{t('dataLists.schema.webhook.relationDefaultPlaceholder', { field: 'SKU' })}</Text>
                        </TouchableOpacity>
                        {relationSearchFieldChoices('products').map((f) => (
                          <TouchableOpacity key={f.id} style={styles.inlineOption} onPress={() => { setProductSearchField(f.id); setProductSearchFieldPickerOpen(false); }}>
                            <Text style={[styles.inlineOptionText, productSearchField === f.id && styles.inlineOptionTextActive]}>{f.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    )}

                    <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.relationContactsLabel')}</Text>
                    <TouchableOpacity
                      style={[styles.selectButton, { flexDirection: rtl.rowDirection }]}
                      onPress={() => setContactSearchFieldPickerOpen((v) => !v)}
                    >
                      <Text style={[styles.selectButtonText, !contactSearchField && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]}>
                        {contactSearchField
                          ? relationSearchFieldChoices('contacts').find((f) => f.id === contactSearchField)?.name || contactSearchField
                          : t('dataLists.schema.webhook.relationDefaultPlaceholder', { field: 'Primary Identifier' })}
                      </Text>
                      <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                    </TouchableOpacity>
                    {contactSearchFieldPickerOpen && (
                      <View style={styles.inlineOptions}>
                        <TouchableOpacity style={styles.inlineOption} onPress={() => { setContactSearchField(''); setContactSearchFieldPickerOpen(false); }}>
                          <Text style={[styles.inlineOptionText, !contactSearchField && styles.inlineOptionTextActive]}>{t('dataLists.schema.webhook.relationDefaultPlaceholder', { field: 'Primary Identifier' })}</Text>
                        </TouchableOpacity>
                        {relationSearchFieldChoices('contacts').map((f) => (
                          <TouchableOpacity key={f.id} style={styles.inlineOption} onPress={() => { setContactSearchField(f.id); setContactSearchFieldPickerOpen(false); }}>
                            <Text style={[styles.inlineOptionText, contactSearchField === f.id && styles.inlineOptionTextActive]}>{f.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    )}
                  </>
                )}

                <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, fontSize: typography.fontSize.sm, marginTop: spacing.md }]}>{t('dataLists.schema.webhook.syncSection')}</Text>
                <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.syncIntervalHint')}</Text>
                <SchedulePicker value={webhookSyncRecurrence} onChange={setWebhookSyncRecurrence} />

                <View style={[styles.webhookToggleRow, { flexDirection: rtl.rowDirection }]}>
                  <View style={styles.webhookToggleInfo}>
                    <Text style={[styles.webhookToggleLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.triggerOutboundWebhooksLabel')}</Text>
                    <Text style={[styles.webhookToggleHint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.triggerOutboundWebhooksHint')}</Text>
                  </View>
                  <Switch
                    value={triggerOutboundWebhooks}
                    onValueChange={setTriggerOutboundWebhooks}
                    trackColor={{ false: colors.border, true: (colors.primary as any) + '60' }}
                    thumbColor={triggerOutboundWebhooks ? colors.primary : colors.textLight}
                  />
                </View>
                {!!webhookSyncRecurrence && (
                  <View style={{ flexDirection: rtl.rowDirection, marginTop: spacing.sm }}>
                    <View style={{ flex: 1, marginRight: spacing.xs }}>
                      <Input
                        label={t('dataLists.schema.webhook.maxRetriesLabel')}
                        value={webhookMaxRetries}
                        onChangeText={setWebhookMaxRetries}
                        placeholder="3"
                        keyboardType="numeric"
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Input
                        label={t('dataLists.schema.webhook.retryDelayLabel')}
                        value={webhookRetryDelaySeconds}
                        onChangeText={setWebhookRetryDelaySeconds}
                        placeholder="60"
                        keyboardType="numeric"
                      />
                    </View>
                  </View>
                )}

                {showRunsTab && (
                  <TouchableOpacity onPress={() => setActiveSchemaTab('runs')} style={{ marginTop: spacing.sm }}>
                    <Text style={[styles.hint, { textAlign: rtl.textStart, color: colors.primary }]}>{t('dataLists.schema.webhook.viewRuns')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}

            </>
            )}

            {effectiveSchemaTab === 'runs' && showRunsTab && webhookSyncTaskId && (
              <DataListSyncRuns taskId={webhookSyncTaskId} scheduled={!!webhookSyncRecurrence} />
            )}

            {effectiveSchemaTab === 'fields' && columnsSource === 'custom' && (
              <>
                <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
                  <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('dataLists.schema.columnsSection')}</Text>
                  <TouchableOpacity style={[styles.addColumnButton, { flexDirection: rtl.rowDirection }]} onPress={addColumn}>
                    <Ionicons name="add" size={18} color={colors.primary} />
                    <Text style={styles.addColumnText}>{t('dataLists.schema.addColumn')}</Text>
                  </TouchableOpacity>
                </View>

                {columns.map((col, index) => renderColumn(col, index))}
              </>
            )}

            {effectiveSchemaTab === 'access' && (
            <>
            {/* Assignment */}
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, marginTop: spacing.lg }]}>{t('dataLists.schema.assignedSection')}</Text>
            <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.assignedHint')}</Text>

            <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.assignUsers')}</Text>
            <TouchableOpacity style={[styles.selectButton, { flexDirection: rtl.rowDirection }]} onPress={() => setUserPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !assignedUserIds.length && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]}>
                {assignedUserIds.length ? t('dataLists.schema.usersSelected', { count: assignedUserIds.length }) : t('dataLists.schema.selectUsers')}
              </Text>
              <Ionicons name="chevron-down" size={18} color={colors.textLight} />
            </TouchableOpacity>

            <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('dataLists.schema.assignDepartments')}</Text>
            <TouchableOpacity style={[styles.selectButton, { flexDirection: rtl.rowDirection }]} onPress={() => setDepartmentPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !assignedDepartmentIds.length && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]}>
                {assignedDepartmentIds.length ? t('dataLists.schema.usersSelected', { count: assignedDepartmentIds.length }) : t('dataLists.schema.selectDepartments')}
              </Text>
              <Ionicons name="chevron-down" size={18} color={colors.textLight} />
            </TouchableOpacity>
            </>
            )}
          </ScrollView>
          </>
        )}

        <BusinessUserPicker
          visible={userPickerOpen}
          onClose={() => setUserPickerOpen(false)}
          onConfirm={(ids) => setAssignedUserIds(ids)}
          mode="multiple"
          source="users"
          selectedUserIds={assignedUserIds}
          businessId={currentBusiness?._id}
        />
        <BusinessUserPicker
          visible={departmentPickerOpen}
          onClose={() => setDepartmentPickerOpen(false)}
          onConfirm={(ids) => setAssignedDepartmentIds(ids)}
          mode="multiple"
          source="departments"
          selectedUserIds={assignedDepartmentIds}
          businessId={currentBusiness?._id}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  content: { flex: 1 },
  contentInner: { padding: spacing.lg, paddingBottom: Platform.OS !== 'web' ? 120 : spacing.xl },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: spacing.lg, marginBottom: spacing.sm },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  addColumnButton: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: designTokens.borderRadius.sm, backgroundColor: String(colors.primary) + '10' },
  addColumnText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  columnCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  columnHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm },
  arrayMappingCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginTop: spacing.xs,
  },
  arrayMappingBadgeRow: { alignItems: 'center', gap: spacing.xs, marginBottom: spacing.sm },
  arrayMappingBadgeText: { fontSize: typography.fontSize.xs, color: colors.primary as string, fontFamily: typography.fontFamily.semibold, textTransform: 'uppercase', letterSpacing: 0.5 },
  columnIndex: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.semibold },
  fieldLabel: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.secondary, marginTop: spacing.sm, marginBottom: spacing.xs },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular, marginTop: spacing.xs / 2, marginBottom: spacing.xs },
  webhookToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    marginTop: spacing.md,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  webhookToggleInfo: { flex: 1 },
  webhookToggleLabel: { fontSize: typography.fontSize.base, fontWeight: '500', color: colors.textDark, fontFamily: typography.fontFamily.medium },
  webhookToggleHint: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular, marginTop: 2 },
  resyncColumnsButton: { flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: spacing.sm, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.primary, marginBottom: spacing.sm },
  resyncColumnsText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
  },
  selectButtonDisabled: { backgroundColor: colors.lightGray, opacity: 0.7 },
  selectButtonText: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  selectButtonPlaceholder: { color: colors.textLight },
  inlineOptions: {
    marginTop: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
  },
  inlineOption: { padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border },
  inlineOptionText: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  inlineOptionTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  toggleRow: { marginTop: spacing.sm },
  minMaxRow: { flexDirection: 'row', gap: spacing.md },
  typeChipsScroll: { marginTop: spacing.sm },
  typeChipsRow: { gap: spacing.xs, paddingVertical: 2 },
  typeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.full ?? 999,
    backgroundColor: colors.lightGray,
  },
  typeChipActive: { backgroundColor: colors.primary },
  typeChipDisabled: { opacity: 0.4 },
  typeChipText: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.medium },
  typeChipTextActive: { color: colors.white },
  repeaterBuilder: { marginTop: spacing.sm, gap: spacing.sm },
  repeaterFieldRow: { alignItems: 'center', gap: spacing.xs },
  repeaterFieldTypeScroll: { maxWidth: 220 },
  repeaterFieldRemove: { padding: spacing.xs },
  flagsRow: { gap: spacing.xs, marginTop: spacing.sm },
  flagPill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 1.5,
    borderRadius: designTokens.borderRadius.full ?? 999,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  flagPillActive: { backgroundColor: String(colors.primary) + '15', borderColor: colors.primary },
  flagPillText: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.medium },
  flagPillTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  schemaTabsRow: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
    paddingHorizontal: spacing.md,
  },
  schemaTab: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    marginEnd: spacing.md,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  schemaTabActive: {
    borderBottomColor: colors.primary,
  },
  schemaTabText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
  },
  schemaTabTextActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  advancedToggle: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: spacing.sm, paddingVertical: spacing.xs },
  advancedToggleText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, fontFamily: typography.fontFamily.medium },
  advancedSection: { marginTop: spacing.xs },
});
